import React from 'react';
import { useTheme } from '../context/ThemeContext';
import useSponsorImpression from '../hooks/useSponsorImpression';
import useWindowWidth from '../hooks/useWindowWidth';

/**
 * One sponsored block, everywhere.
 *
 * "Presented by" was written out by hand in three places — the homepage
 * column, the event page and the magazine — each with its own markup, its own
 * label and no way to count anything. Three copies of an advert is three
 * places to forget the disclosure, and US and Canadian advertising rules
 * require one: an ad a reader cannot tell is an ad is the problem the rules
 * exist about. The label is not optional here, it is a prop with a default.
 *
 * TRACKING. Views and clicks together are what a sponsorship is sold on.
 * - A view is counted when the block is actually ON SCREEN, not when the page
 *   renders it. A block below the fold that nobody scrolled to was not an
 *   impression, and billing for one is the kind of thing that ends a partner
 *   relationship. IntersectionObserver decides; the server only counts.
 * - A click goes through goPath (/go/:slug on our own domain), never straight
 *   to the partner. A click counted only in somebody else's analytics is a
 *   click we cannot invoice for.
 *
 * Both are best-effort. A blocked tracker or a failed request must never stop
 * the block rendering or the link working — the advert is the product, the
 * counting is bookkeeping.
 *
 * THREE LAYOUTS, ONE LANGUAGE. Same border, same gold action, same label,
 * laid out for the space it is given:
 *
 *   inline  a compact strip. A logo and a name on one row.
 *   banner  SPLIT above 560px — picture hard against the card's edge, copy
 *           in the middle, a solid gold button holding the right edge.
 *           STACKED below it, because the split genuinely breaks on a phone:
 *           the picture takes a third, the button needs ~150px, and the copy
 *           is crushed into what is left.
 *
 * There was a third, `column`, for a homepage slot I had misread as a grid
 * cell. It is a full-width band alternating with a horizontal newsletter
 * strip, so it takes the banner like everywhere else and the variant was
 * removed rather than left as an option nothing picks.
 *
 * The stacked form lays the copy OVER the picture behind a scrim rather than
 * under it. It reads as a designed placement instead of a photo with a
 * caption, and the scrim is what makes it safe: we do not control what a
 * partner uploads, so the text can never land on a bright patch.
 */
export default function SponsoredBlock({
  id,
  sponsorId,
  name,
  sponsorName,
  logo,
  sponsorLogo,
  image,
  headline,
  buttonText,
  goPath,
  website,
  sponsorWebsite,
  label = 'Sponsored',
  variant = 'inline',
  style = {},
}) {
  const { VB } = useTheme();
  const width = useWindowWidth();

  /*
   * Takes the API shape or the generic one.
   *
   * GET /sponsors/placement/:placement answers with sponsorName/sponsorLogo/
   * sponsorWebsite/id, so a caller can spread the response straight in. A
   * caller holding a different source — the event page's PartnerApplication,
   * say — passes name/logo/website instead.
   */
  const slotId = sponsorId || id;
  const title = name || sponsorName;
  const mark = logo || sponsorLogo;
  const site = website || sponsorWebsite;

  const ref = useSponsorImpression(slotId);

  if (!title && !mark && !headline && !image) return null;

  const href = goPath || site || null;
  const isTracked = Boolean(goPath);

  // 560 is where the split stops working, measured rather than picked: the
  // picture takes 34%, the button needs about 150px, and below this the copy
  // between them has nowhere left to go.
  const SPLIT_MIN = 560;
  const banner = variant === 'banner';
  const split = banner && width >= SPLIT_MIN;

  /*
   * Only what all three share. Each layout sets its OWN direction below.
   *
   * This used to carry flexDirection: 'column' as a base that the split
   * branch never overrode, so the wide banner stacked: picture on top, copy
   * under it, button stranded to the right of a large empty middle. The
   * design was right and the code did something else, and it shipped to a
   * screenshot because what I checked was a hand-written mock-up of the
   * layout rather than the component itself.
   */
  const shell = {
    display: 'flex',
    borderRadius: 14,
    border: `1px solid ${VB.borderDark}`,
    background: VB.cardBgFaint,
    overflow: 'hidden',
    minWidth: 0,
    ...style,
  };

  /*
   * THE BANNER IS CAPPED AND CENTRED, and this is the whole answer to the
   * empty space.
   *
   * Spanning the grid looked right in principle — line up with the content
   * around it — and in practice a 1250px card holding a name and one line
   * of copy is mostly gap, with the button marooned at the far edge. No
   * arrangement of those three things fills that width. Widening the
   * picture instead just makes the advert dominate again, which is where
   * this started.
   *
   * So the card is sized to its CONTENT: 860px, centred. At that width the
   * picture is ~290px and the copy ~530px, which a name, a line and a
   * button actually fill. It reads as a deliberate unit rather than a
   * stretched one, and it is the shape most publications land on for
   * exactly this reason.
   */
  const bannerWidth = { width: '100%', maxWidth: 860, marginLeft: 'auto', marginRight: 'auto' };

  const Label = (
    <span style={{
      fontSize: 10, fontWeight: 800, letterSpacing: 1.6, textTransform: 'uppercase',
      color: VB.snowMuted, whiteSpace: 'nowrap', flexShrink: 0,
    }}>
      {label}
    </span>
  );

  const Action = href && (
    <span style={{
      display: 'inline-block', background: VB.gold, color: '#1A1208',
      fontSize: 12.5, fontWeight: 800, borderRadius: 10, padding: '11px 18px',
      whiteSpace: 'nowrap', flexShrink: 0,
    }}>
      {buttonText || 'Learn more'}
    </span>
  );

  // ── SPLIT: wide banner. The picture is flush to the card's edge, which is
  // what makes it read as a bought placement rather than an inserted image.
  if (split) {
    return (
      <Wrap href={href} isTracked={isTracked} innerRef={ref} style={{ ...shell, ...bannerWidth, flexDirection: 'row', alignItems: 'stretch', minHeight: 150 }}>
        {image && (
          <img src={image} alt="" style={{ width: '34%', maxWidth: 300, objectFit: 'cover', flexShrink: 0, display: 'block', alignSelf: 'stretch' }} />
        )}
        <span style={{
          flex: 1, minWidth: 0, display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', gap: 20, padding: '18px 20px',
        }}>
          <span style={{ minWidth: 0 }}>
            {Label}
            {!image && mark && (
              <img src={mark} alt={title || 'Sponsor'} style={{ maxHeight: 30, maxWidth: 140, objectFit: 'contain', display: 'block', margin: '8px 0 2px' }} />
            )}
            {title && (
              <span style={{ display: 'block', fontSize: 19, fontWeight: 700, color: VB.snowPrimary, letterSpacing: '-0.01em', marginTop: 7, lineHeight: 1.2 }}>
                {title}
              </span>
            )}
            {headline && (
              <span style={{
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                fontSize: 13, color: VB.snowSecondary, lineHeight: 1.45, marginTop: 5,
              }}>
                {headline}
              </span>
            )}
          </span>
          {Action}
        </span>
      </Wrap>
    );
  }

  // ── STACKED: phone and narrow tablet.
  if (banner) {
    return (
      <Wrap
        href={href}
        isTracked={isTracked}
        innerRef={ref}
        style={{
          ...shell,
          ...bannerWidth,
          flexDirection: 'column',
          justifyContent: 'flex-end',
          position: 'relative',
          minHeight: 170,
          /*
           * THE PICTURE IS A BACKGROUND, NOT AN <img>, and that is the fix
           * rather than a preference.
           *
           * As an absolutely-positioned <img> with inset: 0 AND height: 100%
           * it is over-constrained: inside an auto-height flex parent the
           * percentage has nothing to resolve against, the intrinsic size
           * leaks through, and the homepage column rendered 969px tall
           * instead of 260. A background has no intrinsic size, so the box
           * is decided by minHeight and the copy and nothing else.
           *
           * No alt is lost: it was alt="" already. The picture is
           * decoration; the label and the name carry the meaning.
           */
          ...(image ? {
            backgroundImage: `url("${String(image).replace(/"/g, '%22')}")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          } : {}),
        }}
      >
        {/* The scrim. We do not control the creative, so the text can never
            be left sitting on a bright patch of it. */}
        {image && (
          <span style={{ position: 'absolute', inset: 0, background: `linear-gradient(0deg, ${VB.darkCard} 16%, rgba(21,18,16,0.84) 54%, rgba(21,18,16,0.22) 100%)` }} />
        )}
        <span style={{ position: 'relative', padding: '16px 18px', display: 'block' }}>
          {Label}
          {!image && mark && (
            <img src={mark} alt={title || 'Sponsor'} style={{ maxHeight: 30, maxWidth: 140, objectFit: 'contain', display: 'block', margin: '10px 0 2px' }} />
          )}
          {title && (
            <span style={{ display: 'block', fontSize: 18, fontWeight: 700, color: VB.snowPrimary, letterSpacing: '-0.01em', marginTop: 8, lineHeight: 1.2 }}>
              {title}
            </span>
          )}
          {headline && (
            <span style={{
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
              fontSize: 13, color: VB.snowSecondary, lineHeight: 1.45, marginTop: 5,
            }}>
              {headline}
            </span>
          )}
          {href && <span style={{ display: 'block', marginTop: 14 }}>{Action}</span>}
        </span>
      </Wrap>
    );
  }

  // ── INLINE: the compact strip, unchanged.
  return (
    <Wrap href={href} isTracked={isTracked} innerRef={ref} style={{ ...shell, flexDirection: 'row', alignItems: 'center', gap: 12, padding: '12px 16px', borderRadius: 12 }}>
      {Label}
      {mark && (
        <img src={mark} alt={title || 'Sponsor'} style={{ maxHeight: 28, maxWidth: 120, objectFit: 'contain', flexShrink: 0 }} />
      )}
      <span style={{ minWidth: 0 }}>
        {title && <span style={{ fontSize: 13, fontWeight: 700, color: VB.snowPrimary, display: 'block' }}>{title}</span>}
        {headline && <span style={{ fontSize: 13, color: VB.snowSecondary, display: 'block', lineHeight: 1.5 }}>{headline}</span>}
      </span>
      {href && (
        <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 700, color: VB.gold, whiteSpace: 'nowrap', flexShrink: 0 }}>
          {buttonText || 'Learn more'} →
        </span>
      )}
    </Wrap>
  );
}

/**
 * The link, or a plain box when there is nowhere to go.
 *
 * A tracked link is same-origin and must NOT open a new tab: /go/ forwards
 * on, so the tab it opened would be left empty behind the partner's site.
 */
function Wrap({ href, isTracked, innerRef, style, children }) {
  if (!href) return <div ref={innerRef} style={style}>{children}</div>;
  return (
    <a
      ref={innerRef}
      href={href}
      {...(isTracked ? {} : { target: '_blank' })}
      rel={isTracked ? 'sponsored' : 'noopener noreferrer sponsored'}
      style={{ ...style, textDecoration: 'none', cursor: 'pointer' }}
    >
      {children}
    </a>
  );
}
